import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { randomUUID } from '@ag-ui/client';
import { receiveAIV2Handoff } from 'shared/store/actions/ai-v2.actions';
import { getOrganizationId } from 'shared/store/selectors/organization.selector';
import { stashHandoff, toHandoffMessages } from 'shared/panda/handoff-bus';
import { toSearchAnswerView, usePandaSearchAnswer } from 'shared/panda/search-answer';
import type {
  LiveSearchAnswerRun,
  OpenInChatOptions,
  PandaSearchAnswer,
  SearchAnswerSnapshot,
  SearchAnswerSubmission,
  UsePandaSearchAnswerOptions,
} from 'shared/panda/search-answer';
import {
  keepLiveSearchAnswer,
  recallSearchAnswer,
  recallSearchAnswerConversation,
  rememberSearchAnswer,
  rememberSearchAnswerConversation,
  searchAnswerKey,
  takeLiveSearchAnswer,
} from './search-answer-cache';

/**
 * Hands a cached answer to the chat under the search's own thread id — search and
 * chat both set session = thread in Langfuse, so reusing it (with the run id as
 * `parentRunId`) joins the two into one session.
 */
export function openCachedConversation(
  key: string | null,
  cached: SearchAnswerSnapshot | null,
  options?: OpenInChatOptions,
): string {
  const remembered = key ? recallSearchAnswerConversation(key) : null;
  if (remembered) return remembered;
  // Safe to reuse: a cache hit with no remembered conversation means the thread was never handed off.
  const conversationId = cached?.threadId || randomUUID();
  // No snapshot: open the conversation plain — an empty handoff would wipe whatever the chat holds.
  if (cached) {
    stashHandoff(conversationId, {
      kind: 'complete',
      messages: toHandoffMessages(cached.messages),
      parentRunId: cached.runId || undefined,
      acceptOffer: options?.acceptOffer,
    });
  }
  if (key) rememberSearchAnswerConversation(key, conversationId);
  return conversationId;
}

/**
 * `usePandaSearchAnswer` behind the answer cache: opening a result unmounts the
 * results page, and coming back would re-ask Panda. A settled answer is shown
 * from the cache; one still streaming is picked up where it is.
 */
export function useCachedPandaSearchAnswer(
  options: UsePandaSearchAnswerOptions,
): PandaSearchAnswer {
  const { submission, onHasAnswerChange } = options;
  const dispatch = useDispatch();
  const organizationId: string = useSelector(getOrganizationId) ?? '';
  const key = submission ? searchAnswerKey({ organizationId, ...submission }) : null;
  // Looked up once per submission: remembering this run's own answer must not hold it.
  const cached = useMemo(() => (key ? recallSearchAnswer(key) : null), [key]);

  // Keyed on the run's own submission: a resubmit may have moved the card on by the time a run ends.
  const keyOf = useCallback(
    (ran: SearchAnswerSubmission) => searchAnswerKey({ organizationId, ...ran }),
    [organizationId],
  );
  const rememberEndedRun = useCallback(
    (snapshot: SearchAnswerSnapshot, ran: SearchAnswerSubmission) =>
      rememberSearchAnswer(keyOf(ran), snapshot),
    [keyOf],
  );
  const liveRun = useMemo(
    () => ({
      keep: (run: LiveSearchAnswerRun, ran: SearchAnswerSubmission) =>
        keepLiveSearchAnswer(keyOf(ran), run),
      take: (ran: SearchAnswerSubmission) => takeLiveSearchAnswer(keyOf(ran)),
    }),
    [keyOf],
  );

  const answer = usePandaSearchAnswer({
    ...options,
    enabled: !cached,
    onRunEnded: rememberEndedRun,
    liveRun,
    onHasAnswerChange: cached ? undefined : onHasAnswerChange,
  });
  const { showMore } = answer;

  const cachedView = cached ? toSearchAnswerView(cached) : null;
  const cachedHasAnswer = cachedView?.hasAnswer ?? null;
  useEffect(() => {
    if (cachedHasAnswer !== null) onHasAnswerChange?.(cachedHasAnswer);
  }, [cachedHasAnswer, onHasAnswerChange]);

  // A repeat on an adopted run reopens its conversation: a new stash would abort the run it is still on.
  const showMoreRemembered = useCallback(
    (options?: OpenInChatOptions): string => {
      const remembered = key ? recallSearchAnswerConversation(key) : null;
      if (remembered) {
        dispatch(receiveAIV2Handoff({ conversationId: remembered }));
        return remembered;
      }
      const conversationId = showMore(options);
      if (key) rememberSearchAnswerConversation(key, conversationId);
      return conversationId;
    },
    [key, showMore, dispatch],
  );

  // Served from the cache: reopen the conversation it already went to, or hand the settled answer over.
  const openCachedInChat = useCallback(
    (options?: OpenInChatOptions) => openCachedConversation(key, cached, options),
    [key, cached],
  );

  const showMoreCached = useCallback(
    (options?: OpenInChatOptions): string => {
      const conversationId = openCachedInChat(options);
      dispatch(receiveAIV2Handoff({ conversationId }));
      return conversationId;
    },
    [openCachedInChat, dispatch],
  );

  if (!cached || !cachedView) return { ...answer, showMore: showMoreRemembered };

  return {
    ...cached,
    ...cachedView,
    messageId: cached.traceId ?? cached.threadId,
    openInChat: openCachedInChat,
    showMore: showMoreCached,
  };
}
